import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import { FilterGroupingEnum } from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import Typography from '@lib/components/Typography/Typography';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import TopNavigationHeader from '@lib/layouts/AppLayout/TopNavigationHeader/TopNavigationHeader';
import LinkedDocuments, {
  LinkedDocumentsVariant,
} from '@lib/components/LinkedDocuments';
import { APP_URLS } from 'constants/urls';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import styles from './TaskCompletionForm.module.scss';

interface Props {
  actionsComponent?: React.JSX.ElementType;
  defaultValues?: Values;
  disableAwayConfirmation?: boolean;
  error?: ErrorLike;
  fields: FieldItem[];
  formRef: React.RefObject<FormRef | undefined>;
  formTemplateId?: string;
  hasVisibleFields?: boolean;
  id: string;
  isViewLocked?: boolean;
  loading: boolean;
  onMarkedAsRead?: (
    documentationId: string,
    versionId: string,
    versionViewId: string,
  ) => void;
  onSubmit?: (values: Values) => void;
  taskCompletionError?: ErrorLike;
  taskCompletionLoading?: boolean;
  taskGroupKey?: string;
}

export default function TaskCompletionForm(props: Props) {
  const { t } = useTranslation();
  const {
    actionsComponent,
    defaultValues = {},
    disableAwayConfirmation,
    error,
    fields,
    formRef,
    formTemplateId,
    hasVisibleFields = true,
    id,
    isViewLocked,
    loading,
    onMarkedAsRead,
    onSubmit,
    taskCompletionError,
    taskCompletionLoading,
    taskGroupKey,
  } = props;

  const isView = actionsComponent === undefined || onSubmit === undefined;

  const onSubmitHandler = (values: Values) =>
    onSubmit && onSubmit({ ...defaultValues, ...values });

  if (error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
      />
    );
  }

  return (
    <>
      <TopNavigationHeader
        title={isView ? t('view-form') : t('form-completing')}
        backUrl={APP_URLS.app.tasks.view.getDynamicPath({ pathParts: { id } })}
      />
      <PageBody withoutHeader>
        {loading ? (
          <CircleLoader />
        ) : (
          <AppContentMaxWidth>
            {!isViewLocked && taskGroupKey && (
              <LinkedDocuments
                label={t('task-related-documents')}
                variant={LinkedDocumentsVariant.WithAccordion}
                filters={[
                  {
                    documentationTaskGroupsGroupKey: {
                      grouping: FilterGroupingEnum.And,
                      predicate: { in: [taskGroupKey] },
                    },
                  },
                ]}
                getDetailsUrl={(doc) =>
                  APP_URLS.app.documentation.view.getDynamicPath({
                    pathParts: { id: doc.id },
                  })
                }
                useFavoritesTool
                useMarkAsReadTool
                showUnavailableState
                onMarkedAsRead={onMarkedAsRead}
              />
            )}
            {!isViewLocked && formTemplateId && (
              <LinkedDocuments
                label={t('form-related-documents')}
                variant={LinkedDocumentsVariant.WithAccordion}
                filters={[
                  {
                    linkedFormTemplatesId: {
                      grouping: FilterGroupingEnum.And,
                      predicate: { in: [formTemplateId] },
                    },
                  },
                ]}
                getDetailsUrl={(doc) =>
                  APP_URLS.app.documentation.view.getDynamicPath({
                    pathParts: { id: doc.id },
                  })
                }
                useFavoritesTool
                useMarkAsReadTool
                showUnavailableState
                onMarkedAsRead={onMarkedAsRead}
              />
            )}
            {!isView && hasVisibleFields && (
              <div className={styles.formInfo}>
                <Typography variant="body">
                  {t('indicates-required-question')}
                </Typography>
              </div>
            )}
            <Form
              actionsComponent={actionsComponent}
              defaultValues={defaultValues}
              disableAwayConfirmation={disableAwayConfirmation}
              fields={fields}
              formId="completion"
              formRef={formRef}
              graphQLErrors={getGraphQLErrors(taskCompletionError)}
              loading={taskCompletionLoading}
              onSubmit={onSubmitHandler}
            />
          </AppContentMaxWidth>
        )}
      </PageBody>
    </>
  );
}
